import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, RotateCw } from "lucide-react";
import { houseFor } from "@/lib/houses";
import { HANDDRAWN } from "@/constants/testIds";
import CardFace from "@/components/CardFace";

/**
 * FanWheel — a rotational wheel selector for the 78 traditional tarot cards.
 *
 * Sections: Major Arcana · Cups · Swords · Wands · Pentacles.
 * The active section title displays in large elegant gold above the wheel.
 * Cards are laid out along a horizontal arc using perspective + rotateY so
 * the row *feels* like a slowly turning wheel. Seekers scroll / drag to
 * rotate through cards; tapping a card locks it and reveals the Upright /
 * Reversed toggle. A confirm click emits the final selection.
 *
 * Props:
 *   sections      — [{id, name, cards: [string]}]
 *   element       — user's House ("Fire" | "Water" | "Air" | "Earth")
 *   position      — string (e.g., "Past") — displayed as the current draw slot
 *   onCancel()
 *   onConfirm({title, upright})
 */
export default function FanWheel({
  sections,
  element,
  position,
  onCancel,
  onConfirm,
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [selected, setSelected] = useState(null); // {title}
  const [upright, setUpright] = useState(true);
  const railRef = useRef(null);

  const activeSection = sections[activeIdx];
  const house = houseFor(element);

  // Reset selection when section changes
  useEffect(() => {
    setSelected(null);
    setUpright(true);
    // Recenter scroll
    if (railRef.current) railRef.current.scrollLeft = 0;
  }, [activeIdx]);

  const commit = () => {
    if (!selected) return;
    onConfirm({ title: selected.title, upright });
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid={HANDDRAWN.wheelModal}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="fixed inset-0 z-[70] flex flex-col"
        style={{
          background: "rgba(4,4,4,0.94)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        {/* Header row */}
        <div className="flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-6 pb-4">
          <div>
            <div className="font-overline text-[#C8A96E]">Draw · {position}</div>
            <div className="font-heading italic text-lg text-[#F4EFE6]/70 mt-1">
              Choose the card that finds your hand.
            </div>
          </div>
          <button
            data-testid={HANDDRAWN.cancelWheelBtn}
            onClick={onCancel}
            aria-label="Close wheel"
            className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
            style={{
              border: "1px solid rgba(212,175,55,0.35)",
              color: "#D4AF37",
            }}
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {/* Section tabs */}
        <div className="px-6 sm:px-10 lg:px-16">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {sections.map((s, i) => (
              <button
                key={s.id}
                data-testid={HANDDRAWN.sectionTab(s.id)}
                onClick={() => setActiveIdx(i)}
                className="px-4 py-2 rounded-full font-overline text-[11px] tracking-[0.22em] transition-all min-h-[36px]"
                style={{
                  color:
                    i === activeIdx ? "#0B0B0B" : "rgba(244,239,230,0.55)",
                  background:
                    i === activeIdx
                      ? "#D4AF37"
                      : "rgba(15,15,15,0.55)",
                  border:
                    i === activeIdx
                      ? "1px solid #D4AF37"
                      : "1px solid rgba(212,175,55,0.25)",
                  boxShadow:
                    i === activeIdx
                      ? "0 0 22px rgba(212,175,55,0.35)"
                      : "none",
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {/* Section title — large elegant gold */}
        <div className="text-center px-6 mt-4 mb-3">
          <motion.div
            key={activeSection.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="font-heading italic font-light text-[#D4AF37] leading-none"
            style={{
              fontSize: "clamp(1.75rem, 5.5vw, 4.75rem)",
              letterSpacing: "0.01em",
              textShadow: "0 0 40px rgba(212,175,55,0.25)",
            }}
          >
            {activeSection.name}
          </motion.div>
        </div>

        {/* The rotating wheel of cards */}
        <div className="flex-1 flex items-center overflow-hidden py-2">
          <div
            ref={railRef}
            className="w-full overflow-x-auto scroll-smooth"
            style={{
              scrollSnapType: "x mandatory",
              perspective: "1600px",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              className="flex items-center gap-3 sm:gap-6 py-6"
              style={{
                minHeight: "clamp(220px, 34vh, 480px)",
                paddingLeft: "calc(50vw - 70px)",
                paddingRight: "calc(50vw - 70px)",
              }}
            >
              {activeSection.cards.map((title) => {
                const isSelected = selected?.title === title;
                return (
                  <WheelCard
                    key={title}
                    title={title}
                    house={house}
                    isSelected={isSelected}
                    onClick={() => setSelected({ title })}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Selection footer — orientation + confirm */}
        <AnimatePresence>
          {selected && (
            <motion.div
              key={selected.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="px-6 sm:px-10 lg:px-16 py-6 border-t"
              style={{
                borderColor: "rgba(212,175,55,0.2)",
                background:
                  "linear-gradient(180deg, rgba(8,8,8,0) 0%, rgba(8,8,8,0.85) 60%)",
              }}
            >
              <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-6">
                <div>
                  <div className="font-overline text-[#F4EFE6]/50 mb-1">Bound</div>
                  <div className="font-heading italic text-2xl sm:text-3xl text-[#F4EFE6]">
                    {selected.title}
                    <span className="text-[#C8A96E]/80 ml-3 text-lg not-italic">
                      · {upright ? "Upright" : "Reversed"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    data-testid={HANDDRAWN.uprightToggle}
                    onClick={() => setUpright(true)}
                    className="px-4 py-2 rounded-full font-overline text-[11px] tracking-[0.22em] transition-all"
                    style={{
                      color: upright ? "#0B0B0B" : "rgba(244,239,230,0.6)",
                      background: upright ? "#D4AF37" : "rgba(15,15,15,0.55)",
                      border: `1px solid ${upright ? "#D4AF37" : "rgba(212,175,55,0.25)"}`,
                    }}
                  >
                    Upright
                  </button>
                  <button
                    data-testid={HANDDRAWN.reversedToggle}
                    onClick={() => setUpright(false)}
                    className="px-4 py-2 rounded-full font-overline text-[11px] tracking-[0.22em] transition-all inline-flex items-center gap-2"
                    style={{
                      color: !upright ? "#0B0B0B" : "rgba(244,239,230,0.6)",
                      background: !upright ? "#D4AF37" : "rgba(15,15,15,0.55)",
                      border: `1px solid ${!upright ? "#D4AF37" : "rgba(212,175,55,0.25)"}`,
                    }}
                  >
                    <RotateCw size={12} strokeWidth={1.5} />
                    Reversed
                  </button>
                  <button
                    data-testid={HANDDRAWN.confirmCardBtn}
                    onClick={commit}
                    className="btn-sacred btn-sacred-solid"
                  >
                    Bind this card
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

function WheelCard({ title, house, isSelected, onClick }) {
  const testId = HANDDRAWN.wheelCard(title);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="shrink-0 rounded-2xl relative overflow-hidden focus:outline-none transition-all"
      style={{
        scrollSnapAlign: "center",
        width: "clamp(140px, 34vw, 220px)",
        aspectRatio: "3 / 4",
        border: isSelected
          ? "2px solid #D4AF37"
          : "1px solid rgba(212,175,55,0.25)",
        boxShadow: isSelected
          ? "0 0 40px rgba(212,175,55,0.4), 0 22px 60px rgba(0,0,0,0.55)"
          : "0 18px 40px rgba(0,0,0,0.55)",
        transform: isSelected ? "translate3d(0,-14px,0) scale(1.04)" : "translate3d(0,0,0)",
        transition:
          "transform 500ms ease, box-shadow 500ms ease, border-color 300ms ease",
        background: "#080808",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
      }}
    >
      <CardFace
        title={title}
        upright
        size={220}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 0,
          border: "none",
          boxShadow: "none",
        }}
      />
      {isSelected && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(212,175,55,0.16), transparent 65%)",
          }}
        />
      )}
    </button>
  );
}
